import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { T, Icon, fmtKES } from "../businessTheme";

function BarChart({ data }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 14, height: 180, padding: "0 4px" }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 10.5, color: T.n400, fontWeight: 600 }}>
            {d.value >= 1000 ? `${Math.round(d.value / 1000)}k` : d.value}
          </div>
          <div style={{
            width: "100%", maxWidth: 36, borderRadius: "6px 6px 0 0",
            height: `${(d.value / max) * 130}px`,
            background: i === data.length - 1 ? T.g500 : T.g200,
            transition: "height .3s",
          }} />
          <div style={{ fontSize: 11, color: T.n500, fontWeight: 600 }}>{d.month}</div>
        </div>
      ))}
    </div>
  );
}

function CategoryDonut({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let cumulative = 0;
  const radius = 60, circumference = 2 * Math.PI * radius;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      <svg width="160" height="160" viewBox="0 0 160 160" style={{ flexShrink: 0 }}>
        <g transform="translate(80,80) rotate(-90)">
          {data.map((d, i) => {
            const frac = d.value / total;
            const dash = frac * circumference;
            const offset = -cumulative * circumference;
            cumulative += frac;
            return (
              <circle key={i} r={radius} fill="none" stroke={d.color} strokeWidth="22"
                strokeDasharray={`${dash} ${circumference - dash}`} strokeDashoffset={offset} />
            );
          })}
        </g>
        <text x="80" y="76" textAnchor="middle" fontSize="22" fontWeight="700" fill={T.n900} fontFamily={T.sans}>{total}</text>
        <text x="80" y="94" textAnchor="middle" fontSize="11" fill={T.n400} fontFamily={T.sans}>jobs</text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, color: T.n700, fontWeight: 500 }}>{d.label}</span>
            <span style={{ fontSize: 12, color: T.n400 }}>{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricRow({ label, value, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0", borderBottom: `1px solid ${T.n100}` }}>
      <div>
        <div style={{ fontSize: 13, color: T.n700, fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: 11.5, color: T.n400 }}>{sub}</div>}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.n900 }}>{value}</div>
    </div>
  );
}

export default function ReportsSection({ account, userRole, plan }) {
  const [loading, setLoading] = useState(true);
  const [monthlySpend, setMonthlySpend] = useState([]);
  const [jobsByCategory, setJobsByCategory] = useState([]);
  const [performanceSummary, setPerformanceSummary] = useState(null);
  const [dateRange, setDateRange] = useState("6m");
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [pdfUnavailable, setPdfUnavailable] = useState(false);

  const canExport = userRole === "Owner" || userRole === "Admin";

  // FIX: useEffect must be called unconditionally — plan gate moved inside the effect
  useEffect(() => {
    if (!plan?.reportsAccess) return;
    loadReportsData();
  }, [account.id, dateRange, plan?.reportsAccess]);

  // Plan gate — rendered after all hooks
  if (!plan?.reportsAccess) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "64px 24px", textAlign: "center", gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 16, background: T.g50, border: `1px solid ${T.g100}`,
          display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4,
        }}>
          <Icon name="BarChart2" size={24} color={T.g500} strokeWidth={1.7} />
        </div>
        <div>
          <h3 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, color: T.n900, margin: "0 0 8px" }}>
            Detailed reports
          </h3>
          <p style={{ fontSize: 14, color: T.n400, margin: 0, maxWidth: 340, lineHeight: 1.6 }}>
            Detailed reports are available on the Growth plan and above. Upgrade to unlock spend analytics, job breakdowns, and performance summaries.
          </p>
        </div>
        <button style={{
          marginTop: 8, padding: "11px 26px", borderRadius: 10, border: "none",
          background: T.g500, color: T.white, fontSize: 13.5, fontWeight: 600,
          fontFamily: T.sans, cursor: "pointer",
        }}
          onMouseEnter={e => e.currentTarget.style.background = T.g600}
          onMouseLeave={e => e.currentTarget.style.background = T.g500}
        >
          Upgrade to Growth
        </button>
      </div>
    );
  }

  const loadReportsData = async () => {
    setLoading(true);
    try {
      const { data: jobs } = await supabase
        .from("job_requests")
        .select("id, created_at, estimated_price, description, status, payment_status, pro_id")
        .eq("business_id", account.id)
        .order("created_at", { ascending: true });

      const completedJobs = (jobs || []).filter(j => j.status === "completed" && j.payment_status !== "unpaid");

      const spendByMonth = {};
      const categoryCount = {};
      completedJobs.forEach(j => {
        const month = new Date(j.created_at).toLocaleString("en-KE", { month: "short" });
        spendByMonth[month] = (spendByMonth[month] ?? 0) + (j.estimated_price ?? 0);
        const cat = j.description || "Other";
        categoryCount[cat] = (categoryCount[cat] ?? 0) + 1;
      });

      setMonthlySpend(
        Object.entries(spendByMonth).map(([month, value]) => ({ month, value }))
      );

      const palette = ["#1e9254", "#2563eb", "#7c3aed", "#b45309", "#dc2626", "#0891b2"];
      setJobsByCategory(
        Object.entries(categoryCount).map(([label, value], i) => ({
          label, value, color: palette[i % palette.length],
        }))
      );

      // Re-booking = same pro used more than once by this business.
      // "On-time" has no real tracked concept anywhere in the schema
      // (no scheduled-vs-actual-arrival timestamp exists) — left out
      // honestly rather than faked; see note below.
      const jobCountByPro = {};
      completedJobs.forEach(j => {
        if (j.pro_id) jobCountByPro[j.pro_id] = (jobCountByPro[j.pro_id] || 0) + 1;
      });
      const rebookedJobs = completedJobs.filter(j => j.pro_id && jobCountByPro[j.pro_id] > 1).length;

      const { count: disputeCount } = await supabase
        .from("reports")
        .select("id", { count: "exact", head: true })
        .in("booking_id", (jobs || []).map(j => j.id));

      setPerformanceSummary({
        totalCompleted: completedJobs.length,
        onTimeRate: null, // no real data source — rendered as "Not tracked yet" below
        rebookingRate: completedJobs.length > 0 ? Math.round((rebookedJobs / completedJobs.length) * 100) : 0,
        openDisputes: disputeCount || 0,
      });
    } catch (err) {
      console.error("Failed to load reports data:", err);
    } finally {
      setLoading(false);
    }
  };

  const ytdSpend = monthlySpend.reduce((s, d) => s + d.value, 0);

  const handleExportPDF = () => {
    console.warn("PDF export not available — jspdf not installed");
    setPdfUnavailable(true);
  };

  const handleExportCSV = () => {
    const rows = [
      ["Month", "Spend (KES)"],
      ...monthlySpend.map(d => [d.month, d.value]),
      [],
      ["Category", "Jobs"],
      ...jobsByCategory.map(d => [d.label, d.value]),
    ];
    if (performanceSummary) {
      rows.push([]);
      rows.push(["Metric", "Value"]);
      rows.push(["Total completed", performanceSummary.totalCompleted]);
      rows.push(["On-time rate (%)", performanceSummary.onTimeRate]);
      rows.push(["Re-booking rate (%)", performanceSummary.rebookingRate]);
      rows.push(["Open disputes", performanceSummary.openDisputes]);
    }

    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `JiraniApp_Report_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMenuOpen(false);
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { id: "1m", label: "Last month" },
            { id: "3m", label: "3 months" },
            { id: "6m", label: "6 months" },
          ].map(r => (
            <button
              key={r.id}
              onClick={() => setDateRange(r.id)}
              style={{
                padding: "6px 14px", borderRadius: 99, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${dateRange === r.id ? T.g500 : T.n150}`,
                background: dateRange === r.id ? T.g50 : "transparent",
                color: dateRange === r.id ? T.g700 : T.n500,
                fontFamily: T.sans, cursor: "pointer",
              }}>
              {r.label}
            </button>
          ))}
        </div>

        {canExport && (
          <div style={{ position: "relative" }}>
            <button
              onClick={() => { setExportMenuOpen(o => !o); setPdfUnavailable(false); }}
              style={{
                padding: "9px 16px", borderRadius: 10, border: `1.5px solid ${T.n200}`, background: T.white,
                color: T.n700, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 7,
              }}>
              <Icon name="Download" size={14} strokeWidth={2} />
              Export report
              <Icon name="ChevronDown" size={13} strokeWidth={2} color={T.n400} />
            </button>

            {exportMenuOpen && (
              <>
                <div onClick={() => { setExportMenuOpen(false); setPdfUnavailable(false); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                <div style={{
                  position: "absolute", top: "calc(100% + 6px)", right: 0, width: 200,
                  background: T.white, border: `1px solid ${T.n150}`, borderRadius: 10,
                  boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 50, overflow: "hidden",
                }}>
                  <button
                    onClick={handleExportPDF}
                    style={{
                      width: "100%", textAlign: "left", padding: "10px 14px", background: "none",
                      border: "none", cursor: "pointer", fontSize: 12.5, color: T.n700,
                      fontFamily: T.sans, display: "flex", alignItems: "center", gap: 8,
                    }}>
                    <Icon name="FileText" size={13} strokeWidth={2} color={T.n500} />
                    Export as PDF
                  </button>
                  {pdfUnavailable && (
                    <div style={{
                      margin: "0 10px 8px", padding: "7px 10px", borderRadius: 8,
                      background: "#fff7ed", border: "1px solid #fed7aa",
                      fontSize: 11.5, color: "#92400e", lineHeight: 1.5,
                    }}>
                      PDF export is temporarily unavailable.
                    </div>
                  )}

                  <button
                    onClick={handleExportCSV}
                    style={{
                      width: "100%", textAlign: "left", padding: "10px 14px", background: "none",
                      border: "none", cursor: "pointer", fontSize: 12.5, color: T.n700,
                      fontFamily: T.sans, display: "flex", alignItems: "center", gap: 8,
                      borderTop: `1px solid ${T.n100}`,
                    }}>
                    <Icon name="Download" size={13} strokeWidth={2} color={T.n500} />
                    Export as CSV
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* KPI tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 22 }}>
        {[
          { label: "Year-to-date spend", value: fmtKES(ytdSpend) },
          { label: "Avg monthly spend", value: fmtKES(monthlySpend.length > 0 ? Math.round(ytdSpend / monthlySpend.length) : 0) },
          { label: "On-time rate", value: performanceSummary?.onTimeRate == null ? "Not tracked yet" : `${performanceSummary.onTimeRate}%` },
          { label: "Re-booking rate", value: `${performanceSummary?.rebookingRate ?? 0}%` },
        ].map((s, i) => (
          <div key={i} style={{ padding: "16px 18px", borderRadius: 14, border: `1px solid ${T.n150}`, background: T.white }}>
            <div style={{ fontFamily: T.serif, fontSize: 22, color: T.n900, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: T.n400 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16, marginBottom: 16 }}>
        <div style={{ padding: "22px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: T.n900, margin: "0 0 18px" }}>Monthly spend</h3>
          {loading ? (
            <p style={{ fontSize: 13, color: T.n400, textAlign: "center", padding: "40px 0" }}>Loading…</p>
          ) : monthlySpend.length > 0 ? (
            <BarChart data={monthlySpend} />
          ) : (
            <p style={{ fontSize: 13, color: T.n400, textAlign: "center", padding: "40px 0" }}>No spend data yet.</p>
          )}
        </div>
        <div style={{ padding: "22px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: T.n900, margin: "0 0 18px" }}>Jobs by category</h3>
          {loading ? (
            <p style={{ fontSize: 13, color: T.n400, textAlign: "center", padding: "40px 0" }}>Loading…</p>
          ) : jobsByCategory.length > 0 ? (
            <CategoryDonut data={jobsByCategory} />
          ) : (
            <p style={{ fontSize: 13, color: T.n400, textAlign: "center", padding: "40px 0" }}>No job data yet.</p>
          )}
        </div>
      </div>

      {/* Performance summary */}
      <div style={{ padding: "22px", borderRadius: 16, border: `1px solid ${T.n150}`, background: T.white }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: T.n900, margin: "0 0 6px" }}>Performance summary</h3>
        <p style={{ fontSize: 12.5, color: T.n400, margin: "0 0 4px" }}>Across your reserved team and on-demand bookings.</p>
        {performanceSummary ? (
          <>
            <MetricRow label="Total jobs completed" value={String(performanceSummary.totalCompleted)} />
            <MetricRow label="On-time arrival rate" value={performanceSummary.onTimeRate == null ? "Not tracked yet" : `${performanceSummary.onTimeRate}%`} sub="Pro arrived within the agreed window" />
            <MetricRow label="Re-booking rate" value={`${performanceSummary.rebookingRate}%`} sub="Same Pro booked again within 30 days" />
            <MetricRow label="Disputes raised" value={String(performanceSummary.openDisputes)} sub="Open or resolved disputes this period" />
          </>
        ) : (
          <p style={{ fontSize: 13, color: T.n400, textAlign: "center", padding: "24px 0" }}>
            {loading ? "Loading…" : "No performance data yet."}
          </p>
        )}
      </div>
    </div>
  );
}